import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Play, AlertCircle } from 'lucide-react'
import { Sidebar } from '../components/Sidebar'

interface Campaign {
  id: string
  meta_campaign_id: string
  campaign_name: string
  market_type: string
  status: string
  auto_optimize: boolean
  daily_budget: number
  launch_date: string
  last_optimized_at: string
  created_at: string
}

interface AdStats {
  campaign_id: string
  total_ads: number
  active_ads: number
  paused_ads: number
}

export default function MetaCampaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [adStats, setAdStats] = useState<Record<string, AdStats>>({})
  const [loading, setLoading] = useState(true)
  const [logs, setLogs] = useState<string[]>([])
  const [isRunning, setIsRunning] = useState(false)

  useEffect(() => {
    fetchCampaigns()
    fetchAdStats()
  }, [])

  const fetchCampaigns = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('meta_campaigns')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setCampaigns(data || [])
    } catch (error) {
      console.error('Error fetching campaigns:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAdStats = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('meta_ads')
        .select('ad_set_id, status, meta_ad_sets!inner(campaign_id)')

      if (error) throw error

      const stats: Record<string, AdStats> = {}
      data?.forEach((ad: any) => {
        const campaignId = ad.meta_ad_sets.campaign_id
        if (!stats[campaignId]) {
          stats[campaignId] = { campaign_id: campaignId, total_ads: 0, active_ads: 0, paused_ads: 0 }
        }
        stats[campaignId].total_ads++
        if (ad.status === 'active') stats[campaignId].active_ads++
        if (ad.status === 'paused') stats[campaignId].paused_ads++
      })

      setAdStats(stats)
    } catch (error) {
      console.error('Error fetching ad stats:', error)
    }
  }

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 50))
  }

  const handleSyncAndOptimize = async () => {
    setIsRunning(true)
    setLogs([])
    
    try {
      // Step 1: Sync Campaigns
      addLog('🔄 Starting campaign sync...')
      const campaignsResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-meta-campaigns`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      )
      const campaignsResult = await campaignsResponse.json()
      addLog(`✅ Synced ${campaignsResult.total_campaigns || 0} campaigns`)
      await fetchCampaigns()
      
      // Step 2: Sync Ads
      addLog('🔄 Starting ads sync...')
      try {
        const adsResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-meta-ads`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
          }
        )
        
        if (!adsResponse.ok) {
          const errorData = await adsResponse.json()
          addLog(`⚠️ Ads sync failed: ${errorData.error || 'Unknown error'}`)
        } else {
          const adsResult = await adsResponse.json()
          addLog(`✅ Synced ${adsResult.ad_sets_synced || 0} ad sets, ${adsResult.ads_synced || 0} ads`)
          if (adsResult.errors > 0) {
            addLog(`⚠️ ${adsResult.errors} errors occurred during sync (likely rate limits)`)
          }
        }
        await fetchAdStats()
      } catch (syncError: any) {
        addLog(`⚠️ Ads sync error: ${syncError.message}`)
      }
      
      // Step 3: Run Optimization (runs independently of sync success)
      addLog('🎯 Running optimization...')
      const optimizeResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/optimize-meta-ads`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      )
      const optimizeResult = await optimizeResponse.json()
      
      // Log detailed results
      if (optimizeResult.success) {
        addLog(`✅ Optimization complete!`)
        addLog(`   📊 Campaigns analyzed: ${optimizeResult.campaigns_analyzed || 0}`)
        addLog(`   ⏭️  Campaigns skipped: ${optimizeResult.campaigns_skipped || 0}`)
        addLog(`   ⏸️  Ads paused: ${optimizeResult.ads_paused || 0}`)
        
        // Show paused ads details if available
        if (optimizeResult.paused_ads && optimizeResult.paused_ads.length > 0) {
          addLog(`   🎯 Paused ads:`)
          optimizeResult.paused_ads.forEach((ad: any) => {
            addLog(`      • ${ad.campaign_name}: Ad ${ad.ad_name || ad.ad_id}`)
          })
        }
      } else {
        addLog(`⚠️ Optimization completed with warnings`)
        addLog(`   Message: ${optimizeResult.message || 'No details available'}`)
      }
      
      await fetchCampaigns()
      await fetchAdStats()
      
      addLog('🎉 All done!')
    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`)
      console.error('Error in sync & optimize:', error)
    } finally {
      setIsRunning(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading campaigns...</div>
      </div>
    )
  }

  const filteredCampaigns = campaigns.filter(c => (c as any).today_spend >= 0.01)

  return (
    <div className="flex h-screen bg-gray-900">
      <Sidebar />
      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Meta Ad Campaigns</h1>
            <p className="text-gray-400">
              {filteredCampaigns.length} campaigns spending ≥€0.01 today • Automated optimization runs twice daily
            </p>
          </div>
          <div>
            <button
              onClick={handleSyncAndOptimize}
              disabled={isRunning}
              className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed rounded-lg transition font-semibold"
            >
              <Play className={`w-5 h-5 ${isRunning ? 'animate-pulse' : ''}`} />
              {isRunning ? 'Running...' : 'Sync & Optimize'}
            </button>
          </div>
        </div>

        {/* Logs */}
        {logs.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">Activity Log</h2>
            <div className="bg-gray-900 rounded p-4 max-h-64 overflow-y-auto font-mono text-sm">
              {logs.map((log, i) => (
                <div key={i} className="text-gray-300 py-1">{log}</div>
              ))}
            </div>
          </div>
        )}

        {/* Campaigns Table */}
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Ads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Today's Spend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Last Optimized
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredCampaigns.map((campaign) => {
                const stats = adStats[campaign.id]
                const todaySpend = (campaign as any).today_spend || 0
                return (
                  <tr key={campaign.id} className="hover:bg-gray-750">
                    <td className="px-6 py-4">
                      <div className="font-medium">{campaign.campaign_name}</div>
                      <div className="text-sm text-gray-400">{campaign.meta_campaign_id}</div>
                    </td>
                    <td className="px-6 py-4">
                      {stats ? (
                        <div className="flex items-center gap-2">
                          <span className="text-green-400">{stats.active_ads}</span>
                          <span className="text-gray-500">/</span>
                          <span className="text-gray-400">{stats.total_ads}</span>
                          {stats.active_ads < 3 && (
                            <AlertCircle className="w-4 h-4 text-yellow-400" />
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={todaySpend >= 0.01 ? 'text-green-400 font-semibold' : 'text-gray-500'}>
                        €{todaySpend.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {campaign.last_optimized_at
                        ? new Date(campaign.last_optimized_at).toLocaleString()
                        : 'Never'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {filteredCampaigns.length === 0 && campaigns.length > 0 && (
          <div className="text-center py-12 text-gray-400">
            No campaigns spending ≥€0.01 today. Toggle filter to see all campaigns.
          </div>
        )}
        
        {campaigns.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            No campaigns found. Click "Sync Campaigns" to import from Meta.
          </div>
        )}
        </div>
      </div>
    </div>
  )
}
